import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { TenantTypeUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type TenantTypeUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_es: string;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_esError?: string;
  name_en: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_enError?: string;
  name_pt: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_ptError?: string;
  description_es: string;
  description_esSelectedValue?: { displayId: string; id: string } | null;
  onDescription_esChange?: (value: string) => void;
  description_esError?: string;
  description_en: string;
  description_enSelectedValue?: { displayId: string; id: string } | null;
  onDescription_enChange?: (value: string) => void;
  description_enError?: string;
  description_pt: string;
  description_ptSelectedValue?: { displayId: string; id: string } | null;
  onDescription_ptChange?: (value: string) => void;
  description_ptError?: string;
  tenantTypeUpdateStore: TenantTypeUpdateStore;
};

export const TenantTypeUpdateForm: React.FC<TenantTypeUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name_es = "",
      name_esError,
      onName_esChange = () => {},
      name_en = "",
      name_enError,
      onName_enChange = () => {},
      name_pt = "",
      name_ptError,
      onName_ptChange = () => {},
      description_es = "",
      description_esError,
      onDescription_esChange = () => {},
      description_en = "",
      description_enError,
      onDescription_enChange = () => {},
      description_pt = "",
      description_ptError,
      onDescription_ptChange = () => {},
      tenantTypeUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="TenantType"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name_es":
                    onName_esChange?.(valor);
                    break;
                  case "name_en":
                    onName_enChange?.(valor);
                    break;
                  case "name_pt":
                    onName_ptChange?.(valor);
                    break;
                  case "description_es":
                    onDescription_esChange?.(valor);
                    break;
                  case "description_en":
                    onDescription_enChange?.(valor);
                    break;
                  case "description_pt":
                    onDescription_ptChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={tenantTypeUpdateStore.getCardErrors("null")}
              title={langStore.groupTenantTypeLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name_es}
                  error={Boolean(name_esError)}
                  warning={
                    !name_esError &&
                    Boolean(tenantTypeUpdateStore.warnings["name_es"])
                  }
                  helperText={
                    name_esError ||
                    tenantTypeUpdateStore.warnings["name_es"]?.message
                  }
                  label={langStore.TenantTypeNameLabel + " (es)"}
                  onChange={(value: string) => onName_esChange(value)}
                  description={langStore.TenantTypeNameDescription}
                />
                <InputString
                  value={name_en}
                  error={Boolean(name_enError)}
                  warning={
                    !name_enError &&
                    Boolean(tenantTypeUpdateStore.warnings["name_en"])
                  }
                  helperText={
                    name_enError ||
                    tenantTypeUpdateStore.warnings["name_en"]?.message
                  }
                  label={langStore.TenantTypeNameLabel + " (en)"}
                  onChange={(value: string) => onName_enChange(value)}
                  description={langStore.TenantTypeNameDescription}
                />
                <InputString
                  value={name_pt}
                  error={Boolean(name_ptError)}
                  warning={
                    !name_ptError &&
                    Boolean(tenantTypeUpdateStore.warnings["name_pt"])
                  }
                  helperText={
                    name_ptError ||
                    tenantTypeUpdateStore.warnings["name_pt"]?.message
                  }
                  label={langStore.TenantTypeNameLabel + " (pt)"}
                  onChange={(value: string) => onName_ptChange(value)}
                  description={langStore.TenantTypeNameDescription}
                />
                <InputString
                  value={description_es}
                  error={Boolean(description_esError)}
                  warning={
                    !description_esError &&
                    Boolean(tenantTypeUpdateStore.warnings["description_es"])
                  }
                  helperText={
                    description_esError ||
                    tenantTypeUpdateStore.warnings["description_es"]?.message
                  }
                  label={langStore.TenantTypeDescriptionLabel + " (es)"}
                  onChange={(value: string) => onDescription_esChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantTypeDescriptionDescription}
                />
                <InputString
                  value={description_en}
                  error={Boolean(description_enError)}
                  warning={
                    !description_enError &&
                    Boolean(tenantTypeUpdateStore.warnings["description_en"])
                  }
                  helperText={
                    description_enError ||
                    tenantTypeUpdateStore.warnings["description_en"]?.message
                  }
                  label={langStore.TenantTypeDescriptionLabel + " (en)"}
                  onChange={(value: string) => onDescription_enChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantTypeDescriptionDescription}
                />
                <InputString
                  value={description_pt}
                  error={Boolean(description_ptError)}
                  warning={
                    !description_ptError &&
                    Boolean(tenantTypeUpdateStore.warnings["description_pt"])
                  }
                  helperText={
                    description_ptError ||
                    tenantTypeUpdateStore.warnings["description_pt"]?.message
                  }
                  label={langStore.TenantTypeDescriptionLabel + " (pt)"}
                  onChange={(value: string) => onDescription_ptChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.TenantTypeDescriptionDescription}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
